import type { SessionUser } from './types';
import { deriveTintFromKey } from './wagepro_store_session_facts';

/** The minimal claim `auth.identity` (Sign in with Ledger) returns. */
export interface IdentityClaim {
  userId: string;
  email: string;
  /** The name entered at signup, when available — see `deriveSessionFromIdentity`. */
  fullName?: string;
}

/**
 * The scoped firm/entity claim `auth.oauthGrant` returns (see
 * `AuthContextValue['grant']` in `lib/auth/auth_context.tsx`). Only the two
 * fields this module actually surfaces are declared here — `mode`/`scopes`
 * are irrelevant to session derivation.
 */
export interface GrantClaim {
  firmId: string | null;
  entityId: string | null;
}

/**
 * Derives a display name + initials from an OAuth identity claim's email
 * local-part — e.g. `jane.doe@example.com` -> name `Jane Doe`, initials `JD`.
 * Falls back to the first two letters of a single-word local-part (e.g.
 * `admin@example.com` -> `Admin` / `AD`). Also surfaces the REAL
 * `firm_id`/`entity_id` an OAuth grant is bound to (#21929 PR4), when one is
 * provided, and a deterministic avatar `tint` keyed on the identity's
 * `userId` (C1, #22323, via `deriveTintFromKey` --
 * `wagepro_store_session_facts.ts`, the same hash function
 * `wagepro_store_employees.ts` uses for per-employee tints — one source of
 * truth for "how a stable id maps to a tint" app-wide).
 *
 * `name`/`initials`/`firm_id`/`entity_id`/`tint` are identity/grant-derived.
 * `role` and `company_name` are left exactly as the mock session provides
 * them here — they only become real once `deriveSessionFromFacts`
 * (`wagepro_store_session_facts.ts`, unchanged by this pass) overlays a
 * resolved `SessionFacts` on top of this function's output. `tint` is
 * deliberately NOT gated on facts the way `role`/`company_name` are: it only
 * depends on `identity.userId`, so it must be correct as soon as identity
 * resolves — not stuck showing `MOCK_SESSION.tint` for the (however brief)
 * window before facts load, or forever if facts never resolve (e.g. the
 * `no_entity` state main.tsx renders instead of a session at all).
 *
 * Returns `base` unchanged when both `identity` and `grant` are absent (no
 * real session yet) or `identity`'s email has no usable local-part (grant
 * fields are still applied in that case, since they don't depend on the
 * email parse succeeding).
 */
export function deriveSessionFromIdentity(
  base: SessionUser,
  identity: IdentityClaim | undefined,
  grant?: GrantClaim | null,
): SessionUser {
  const withGrant: SessionUser = grant
    ? { ...base, firm_id: grant.firmId, entity_id: grant.entityId }
    : base;

  if (!identity) return withGrant;

  const withTint: SessionUser = {
    ...withGrant,
    tint: deriveTintFromKey(identity.userId, withGrant.tint),
  };

  // Prefer the REAL name entered at signup over guessing one from the email
  // local-part — the guess below exists only for identities that never had
  // a full name to begin with (kept as a graceful fallback, not removed).
  const realNameWords = (identity.fullName ?? '')
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);

  const words =
    realNameWords.length > 0
      ? realNameWords
      : (identity.email.split('@')[0] ?? '')
          .split(/[._-]+/)
          .filter((word) => word.length > 0)
          .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase());

  if (words.length === 0) return withTint;

  const name = words.join(' ');
  const initials =
    words.length >= 2
      ? `${words[0][0]}${words[1][0]}`.toUpperCase()
      : words[0].slice(0, 2).toUpperCase();

  return { ...withTint, name, initials };
}
